import { Button, Input, Modal } from '@sjuni/ui';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/components/FormError';
import { SubmitButton } from '@/components/SubmitButton';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { useSaveResource } from '@/lib/api/resource';
import { applyApiErrors } from '@/lib/forms/applyApiErrors';
import type { TranslatedValue } from '@/lib/forms/translatable';

import type { VocationalContactInput, VocationalContactRow } from './types';

/**
 * docs/01-INFORMATION-ARCHITECTURE.md §13.7 — one coordinator.
 *
 * A modal rather than a page: the record is five fields and there are four of
 * them, so a route per record would be more navigation than editing.
 *
 * §6.9 — "Help text everywhere. Assume the person editing has never used a
 * CMS." The hint under `sort_order` is the example: nothing about the number
 * says it controls the order they appear on the public page.
 */
export interface VocationalContactDialogProps {
  /** Null for a new record. */
  contact: VocationalContactRow | null;
  onClose: () => void;
}

interface Fields {
  role: TranslatedValue;
  person_name: TranslatedValue;
  phone: string;
  email: string;
  sort_order: number;
}

export function VocationalContactDialog({ contact, onClose }: VocationalContactDialogProps) {
  const { t } = useTranslation();

  const [formError, setFormError] = useState<string | null>(null);

  const save = useSaveResource<VocationalContactRow, VocationalContactInput>('vocational-contacts');

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<Fields>({
    defaultValues: {
      role: contact?.role ?? {},
      person_name: contact?.person_name ?? {},
      phone: contact?.phone ?? '',
      email: contact?.email ?? '',
      sort_order: contact?.sort_order ?? 0,
    },
  });

  const role = watch('role');
  const personName = watch('person_name');

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);

    try {
      await save.mutateAsync({
        id: contact?.id,
        input: {
          role: values.role,
          person_name: values.person_name,
          // Empty means "no number", not an empty string — §5's columns are
          // nullable and a blank one would read as a number that is blank.
          phone: values.phone.trim() === '' ? null : values.phone.trim(),
          email: values.email.trim() === '' ? null : values.email.trim(),
          sort_order: Number(values.sort_order),
        },
      });

      onClose();
    } catch (error) {
      const { formError: message, placed } = applyApiErrors(error, setError, [
        'role',
        'role.ka',
        'person_name',
        'phone',
        'email',
        'sort_order',
      ]);

      setFormError(message ?? (placed ? null : t('errors.generic')));
    }
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={contact ? t('vocational.contacts.edit') : t('vocational.contacts.add')}
      closeLabel={t('common.close')}
      className="sm:w-[min(92vw,720px)]"
    >
      <form onSubmit={onSubmit} noValidate className="space-y-5">
        <FormError message={formError} />

        <TranslatableField
          label={t('vocational.contacts.fields.role')}
          hint={t('vocational.contacts.fields.roleHint')}
          name="role"
          value={role}
          onChange={(next) => setValue('role', next, { shouldDirty: true })}
          error={errors.role?.message}
        />

        <TranslatableField
          label={t('vocational.contacts.fields.person')}
          name="person_name"
          value={personName}
          onChange={(next) => setValue('person_name', next, { shouldDirty: true })}
          required={false}
          error={errors.person_name?.message}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            {...register('phone')}
            type="tel"
            label={t('vocational.contacts.fields.phone')}
            hint={t('vocational.contacts.fields.phoneHint')}
            error={errors.phone?.message}
          />

          <Input
            {...register('email')}
            type="email"
            label={t('vocational.contacts.fields.email')}
            error={errors.email?.message}
          />
        </div>

        <Input
          {...register('sort_order', { valueAsNumber: true })}
          type="number"
          min={0}
          label={t('vocational.contacts.fields.order')}
          hint={t('vocational.contacts.fields.orderHint')}
          error={errors.sort_order?.message}
          className="max-w-40"
        />

        <TranslationProgress
          fields={{
            role: { label: t('vocational.contacts.fields.role'), value: role },
            person_name: { label: t('vocational.contacts.fields.person'), value: personName },
          }}
        />

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t('common.cancel')}
          </Button>

          <SubmitButton loading={isSubmitting}>{t('editor.publishing.save')}</SubmitButton>
        </div>
      </form>
    </Modal>
  );
}
